'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { IoIosArrowForward } from 'react-icons/io';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export function HeroReferences({ references }: { references: Reference[] }) {
  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div className="absolute -bottom-[165px] right-0 z-10 flex w-full max-w-[550px] flex-col items-start justify-start gap-6 overflow-visible pl-5 sm:-bottom-[200px] sm:pl-0 xl:-bottom-[250px]">
        <Carousel
          options={options}
          className={{
            container: 'w-full max-w-[876px] gap-4 overflow-visible sm:gap-5 lg:gap-6',
            wrapper: 'w-full max-w-[876px] overflow-visible',
            main: 'w-full max-w-[876px] overflow-visible',
            slide: cn(
              'w-full max-w-[320px] overflow-hidden rounded-[8px] sm:max-w-[360px] md:max-w-[390px] lg:max-w-[426px]',
              'ea-hero-references__slide',
            ),
          }}
          slides={references.slice(0, 2).map(reference => {
            const allImages = [reference.image, ...(reference.images || [])];

            return (
              <div key={reference.id} className="flex w-full flex-col items-start justify-start">
                <div className="relative h-[186px] w-full sm:h-[200px] md:h-[220px] lg:h-[248px]">
                  <p className="absolute left-3 top-4 z-10 rounded-full bg-black/20 px-3 py-1 text-[10px] font-medium uppercase text-white sm:left-6 sm:top-6 sm:px-4 sm:py-2 sm:text-xs">
                    NAŠA POSLEDNÁ REALIZÁCIA
                  </p>
                  <Image
                    src={allImages[0]?.url}
                    alt={resolveImageAlt(allImages?.[0], reference?.name)}
                    width={0}
                    height={0}
                    className="absolute inset-0 z-0 h-full w-full object-cover object-center"
                    sizes="(min-width: 1024px) 426px, (min-width: 768px) 390px, (min-width: 640px) 360px, 320px"
                  />
                </div>
                <div className="flex w-full items-center justify-between bg-white p-4 sm:p-5 lg:p-6">
                  <div className="flex w-full flex-col items-start justify-start gap-2">
                    <Heading tag="h6" title={reference.name} className="line-clamp-1 text-left" />
                    <p className="ea-heading--h6 text-muted-foreground">
                      {reference?.categories[0]?.name}
                    </p>
                  </div>
                  <Button
                    type="button"
                    aria-label={reference.name}
                    variant="outline"
                    size="icon"
                    onClick={handleOpenGallery(allImages, 0)}
                    className="rounded-full"
                  >
                    <IoIosArrowForward size={20} />
                  </Button>
                </div>
              </div>
            );
          })}
        />
        <CarouselControls
          buttonProps={{ variant: 'default', size: 'icon' }}
          className={{
            main: 'hidden min-w-[112px] max-w-[112px] lg:flex',
            button:
              'border-none bg-primary-foreground text-black shadow-none hover:bg-primary-foreground/75',
          }}
          nextIcon={<IoIosArrowForward size={20} />}
          prevIcon={<IoIosArrowForward size={20} className="rotate-180" />}
        />
      </div>
    </>
  );
}
